import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { cn } from '@/lib/utils'

import { NAV, STUDIO_INFO } from '@/constants/studio'

interface StageNavProps {
    /** The path of the page the nav is sitting on, so its link can be marked. */
    active?: string
    /** Laid over the heart instead of above it: no wordmark (the section draws
     *  its own, centred) and no ground, so the canvas keeps the whole stage. */
    floating?: boolean
}

export const StageNav: FC<StageNavProps> = ({ active, floating = false }): ReactNode => {
    const links = NAV.map((link) => {
        const isActive = link.href === active

        return (
            <Link
                key={link.href}
                href={link.href}
                aria-current={isActive ? 'page' : undefined}
                className={cn(
                    'pointer-events-auto text-[10.5px] font-medium uppercase tracking-[0.24em] transition-opacity duration-300',
                    'focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#4EA3FF]',
                    isActive ? 'opacity-100' : 'opacity-45 hover:opacity-100'
                )}
            >
                {link.label}
            </Link>
        )
    })

    /* On the front page the section owns the stage, so the nav is a floating
       overlay that only takes pointer events on the words themselves — a full
       bar across the top would eat the hover on every frame beneath it. */
    if (floating) {
        return (
            <nav
                aria-label='Primary'
                className='pointer-events-none absolute top-0 right-0 z-30 flex items-center gap-5 px-6 py-5 sm:gap-7 sm:px-[2.4vw] sm:py-[2.2vh] animate-in fade-in slide-in-from-top-2 fill-mode-both delay-700 duration-700 ease-out motion-reduce:animate-none'
            >
                {links}
            </nav>
        )
    }

    return (
        <header className='sticky top-0 z-30 border-b border-white/10 bg-[#050506]/85 backdrop-blur-md'>
            <div className='mx-auto flex max-w-[1200px] items-center justify-between gap-6 px-6 py-4 sm:px-10'>
                <Link
                    href='/'
                    className='inline-flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.3em] focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#4EA3FF]'
                >
                    <i aria-hidden className='text-xs not-italic opacity-80'>✳</i>
                    {STUDIO_INFO.mark}
                </Link>

                <nav aria-label='Primary' className='flex items-center gap-5 sm:gap-7'>
                    {links}
                </nav>
            </div>
        </header>
    )
}
